import { useEffect, useRef, useState } from 'react';
import { captureSentryException } from '@/lib/sentry';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useJoinGroup } from '@/hooks/useGroups';
import { useSubscription } from '@/hooks/useSubscription';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import logoOnWhite from '@/assets/logo-on-white.svg';
import { supabase } from '@/integrations/supabase/client';
import {
  PENDING_INVITE_STORAGE_KEY,
  INVITE_CODE_REGEX,
  type PendingInviteIntent,
} from '@/lib/constants';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const sanitiseItemParam = (raw: string | null): string | undefined =>
  raw && UUID_RE.test(raw) ? raw : undefined;

// Persist the invite code in sessionStorage so Auth.tsx can auto-join after
// sign-up / sign-in. sessionStorage (not localStorage) is used intentionally:
// it is scoped to the current tab, so a stolen invite link opened in another
// tab cannot silently auto-join the user. The 20-minute TTL (INVITE_INTENT_TTL_MS)
// checked in Auth.tsx further limits the exposure window. Note that a stored
// XSS payload in *this* page's origin can still read the key, so no sensitive
// data beyond the invite code should ever be persisted here.
const savePendingInviteIntent = (payload: PendingInviteIntent) => {
  sessionStorage.setItem(PENDING_INVITE_STORAGE_KEY, JSON.stringify(payload));
};

/**
 *
 */
export default function InvitePage() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const joinGroup = useJoinGroup();
  const { maxGroups, isPaid } = useSubscription();

  const { data: groupCount } = useQuery({
    queryKey: ['my-groups-count', user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('group_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user!.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });

  const atGroupLimit = !isPaid && maxGroups > 0 && (groupCount ?? 0) >= maxGroups;

  const [status, setStatus] = useState<'loading' | 'joining' | 'success' | 'error' | 'invalid'>(
    'loading',
  );
  const [errorMessage, setErrorMessage] = useState('');
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (authLoading) return;

    const normalizedCode = inviteCode?.trim() ?? '';
    const isInviteCodeValid = INVITE_CODE_REGEX.test(normalizedCode);

    if (!isInviteCodeValid) {
      setStatus('invalid');
      setErrorMessage('This invite link looks invalid or incomplete.');
      sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
      return;
    }

    if (!user) {
      // Store invite intent for the current auth flow and redirect to auth.
      const itemParam = sanitiseItemParam(new URLSearchParams(location.search).get('item'));
      const inviteIntent: PendingInviteIntent = {
        code: normalizedCode,
        createdAt: Date.now(),
        itemId: itemParam,
      };
      savePendingInviteIntent(inviteIntent);
      navigate('/auth', {
        state: {
          from: `/invite/${normalizedCode}`,
          inviteIntent,
        },
      });
      return;
    }

    // Wait for group count to load before attempting join
    if (groupCount === undefined) return;

    if (status === 'loading' && atGroupLimit) {
      setStatus('error');
      setErrorMessage(
        `You've reached your group limit (${groupCount} of ${maxGroups}). Upgrade your plan to join more groups.`,
      );
      sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
      return;
    }

    if (status === 'loading') {
      setStatus('joining');
      joinGroup
        .mutateAsync(normalizedCode)
        .then(async (groupId) => {
          setStatus('success');
          toast.success('Successfully joined the group!');
          sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
          // Mark onboarding complete for Path B users (joined via invite)
          if (user) {
            await supabase
              .from('profiles')
              .update({ has_completed_onboarding: true } as any)
              .eq('user_id', user.id);
          }
          const itemParam = sanitiseItemParam(new URLSearchParams(location.search).get('item'));
          redirectTimerRef.current = setTimeout(() => {
            // Pass newMember=true so GroupView shows the welcome overlay
            const params = new URLSearchParams();
            params.set('newMember', 'true');
            if (itemParam) params.set('item', itemParam);
            navigate(`/groups/${groupId}?${params.toString()}`);
          }, 1500);
        })
        .catch((error) => {
          captureSentryException(error);
          setStatus('error');
          setErrorMessage(error instanceof Error ? error.message : 'Something went wrong.');
          sessionStorage.removeItem(PENDING_INVITE_STORAGE_KEY);
        });
    }
  }, [user, authLoading, inviteCode, navigate, joinGroup, status, groupCount, atGroupLimit, maxGroups]);

  useEffect(() => {
    return () => clearTimeout(redirectTimerRef.current);
  }, []);

  const cardStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 420,
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.xl,
    boxShadow: SHADOW.lg,
    padding: 32,
    textAlign: 'center',
  };

  const LogoBrand = () => (
    <div style={{ marginBottom: 24 }}>
      <img
        src={logoOnWhite}
        alt="Share Shack"
        style={{ width: 36, height: 36, display: 'block', margin: '0 auto' }}
      />
      <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginTop: 4 }}>Share Shack</div>
    </div>
  );

  if (authLoading || status === 'loading' || status === 'joining') {
    return (
      <div
        style={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          background: T.bg,
          padding: 16,
        }}
      >
        <div style={cardStyle}>
          <LogoBrand />
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: T.accentSoft,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <Loader2 style={{ color: T.accent }} className="h-6 w-6 animate-spin" />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
            Joining Group...
          </h2>
          <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
            Please wait while we add you to the group.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div
        style={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          background: T.bg,
          padding: 16,
        }}
      >
        <div style={cardStyle}>
          <LogoBrand />
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: T.successSoft,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <CheckCircle style={{ color: T.success }} className="h-6 w-6" />
          </div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
            Welcome to the Group!
          </h2>
          <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
            Redirecting you to the group...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'center',
        background: T.bg,
        padding: 16,
      }}
    >
      <div style={cardStyle}>
        <LogoBrand />
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: T.dangerSoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <XCircle style={{ color: T.danger }} className="h-6 w-6" />
        </div>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text, margin: '0 0 6px' }}>
          {status === 'invalid' ? 'Invalid Invite Link' : 'Unable to Join Group'}
        </h2>
        <p style={{ fontSize: 13, color: T.textMuted, margin: '0 0 20px' }}>
          {errorMessage || 'Something went wrong.'}
        </p>
        {atGroupLimit && status === 'error' && (
          <button
            onClick={() => navigate('/account')}
            style={{
              width: '100%',
              padding: '10px',
              borderRadius: RADIUS.md,
              background: T.surfaceSubtle,
              color: T.accent,
              border: `1px solid ${T.border}`,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              marginBottom: 8,
              textDecoration: 'underline',
            }}
          >
            Upgrade your plan
          </button>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              width: '100%',
              padding: '12px',
              borderRadius: RADIUS.md,
              background: T.accent,
              color: T.white,
              border: 'none',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              boxShadow: SHADOW.btn.accent,
            }}
          >
            Go to Dashboard
          </button>
          <Link
            to="/auth"
            style={{
              display: 'block',
              padding: '12px',
              borderRadius: RADIUS.md,
              background: T.surfaceSubtle,
              color: T.textSec,
              border: `1px solid ${T.border}`,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Try Different Account
          </Link>
          <Link
            to="/"
            style={{
              display: 'block',
              padding: '11px',
              borderRadius: RADIUS.md,
              background: 'none',
              border: 'none',
              fontSize: 14,
              fontWeight: 500,
              color: T.textMuted,
              textDecoration: 'none',
            }}
          >
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
